/**
 * L'AZIONE PERSISTENTE — che cosa il giocatore ha da fare in un torneo Limulo, letto da QUALUNQUE
 * schermata della TMA.
 *
 * 🔴 **La decisione** (Luca, 2026-09-25T13:14:47Z, allo sportello), testuale:
 *  · L-2195: «A vedendo tutte le opzioni - se/quando si minimizza quella tab e rimangono azioni da
 *    fare diventa B.» — la barra sopra la barra in basso (A), che minimizzata diventa il bottone
 *    flottante (B);
 *  · L-2197: «client subito, rotta in parallelo» — i dati li ricostruisce il client, finche' non
 *    esiste `GET /v1/me/tournaments/actions` (specifica nel KB, portata da tma-orc).
 * Nasce dal collaudo-335acfb4 (25/09): il check-in d'incontro c'era e non si trovava, la finestra
 * e' scaduta e il motore ha sorteggiato. KB: `services/qlash-telegram-app/ui/
 * 2026-09-25-azione-persistente-proposta.md`.
 *
 * ⭐ **Una sola regola, due schermi.** Per gli incontri l'azione e' la FACCIA del bottone unico della
 * scheda (`facciaBottoneOf`, `bottoneUnico.ts`): la barra dice la stessa cosa che il giocatore trova
 * quando entra. Una seconda derivazione qui divergerebbe dalla prima alla prima regola nuova.
 *
 * ⛔ **Nessun ramo guarda l'orologio** (ADR-0158): un'azione compare e sparisce quando lo dice il
 * server. Il conto alla rovescia e' testo, e lo scrive il componente.
 *
 * ⚠️ Pura e senza `import.meta.env`: la esercitano i banchi sotto Node.
 */
import type { LimuloMatch, LimuloRegistrationStatus, LimuloTournament } from '../../types/limulo.ts';
import { bridgeTableIdValido } from '../bridge/ticket.ts';
import { chessMatchIdValido } from '../chess/tavolo.ts';
import { facciaBottoneOf } from './bottoneUnico.ts';
import { crInviteOf, externalMatchDeadOf } from './externalData.ts';
import { matchStateOf } from './match.ts';
import {
  matchActionsOf,
  matchCheckInPlanOf,
  ruoloPropostaOf,
  TORNEO_SCONOSCIUTO,
  torneoDellIncontroOf,
  type LimuloTorneoDellIncontro,
} from './matchActions.ts';
import { openGameTargetOf, type LimuloOpenGameTennisClash } from './openGame.ts';
import { tavoloDaAprireOf } from './tavolo.ts';
import { queueMomentOf, waitingSinceOf, type LimuloQueueTicket } from './code.ts';
import { LIMULO_ROUTE_PREFIX, limuloMatchUrl, limuloTournamentUrl } from './routes.ts';

/**
 * I tipi d'azione — solo cose DA FARE (L-2195: «se rimangono azioni da fare»): l'attesa
 * dell'avversario e la decisione dello staff non sono azioni, e una disputa non scade — terrebbe la
 * barra accesa per sempre (revisione di tma-orc su #385, punto 3). In ordine di URGENZA:
 *  · il check-in dell'incontro ha una scadenza del SERVER (15') e chi non risponde perde a tavolino;
 *  · la conferma di un referto ha 15' di silenzio-assenso;
 *  · l'invito CR: fresco vive ~5', ma in `provider` NON sappiamo quando la partita e' finita — il
 *    motore non scrive gli stati vivi e `opened` non si riscrive — quindi resta «partito» fino al
 *    risultato. ⚠️ Residuo dichiarato (punto 2): per questo non sta in cima e non accelera il ritmo;
 *  · il check-in al torneo, il referto, l'incontro da giocare.
 */
/**
 * 🔴 `sfida` NON e' un'azione del motore (I-252/I-254, follow-up sfide-orcr 28/9 17:07Z): e' una sfida
 * di piattaforma (`GET /v1/challenges`, Limulo) ricevuta e ancora valida, non una voce di
 * qTournaments. Vive nella STESSA lista/barra perche' Luca l'ha chiesto esplicitamente («anche
 * nella barra subito»), e sta IN CODA (revisione sfide-orcr 28/9 19:23Z, bloccante 1): mai davanti
 * a un'azione del server o delle sonde. In testa spostava il check-in d'incontro (che il server mette
 * primo, kb#275) sotto «+N», e nel modo «riga» — una sola riga, senza «+N» — lo rendeva
 * IRRAGGIUNGIBILE. Le righe dei tornei devono restare identiche con o senza sfida: la prima riga
 * e' quella che c'era senza. La lista `GET /v1/challenges` resta l'origine di verita' di una sfida che qui non si vede.
 */
export const LIMULO_TIPI_AZIONE = [
  'checkin-incontro',
  'conferma',
  'invito',
  'checkin-torneo',
  'referto',
  'gioca',
  'sfida',
] as const;
export type LimuloTipoAzione = (typeof LIMULO_TIPI_AZIONE)[number];

/**
 * I tipi che, se compaiono NUOVI mentre la barra e' minimizzata, la RIAPRONO da soli (derivazione
 * (b) del cantiere, confermata da tma-orc 13:16:12Z): sono quelli che si perdono a tavolino se non li
 * vedi. `sfida` si aggiunge per la stessa ragione di `invito`: un invito di 15' perso dietro un
 * bottone minimizzato e' perso per davvero.
 */
export const LIMULO_AZIONI_CHE_RIAPRONO: ReadonlySet<LimuloTipoAzione> = new Set(['invito', 'checkin-incontro', 'sfida']);

export interface LimuloAzionePendente {
  /** `torneo:incontro:tipo` — stabile fra i giri; un cambio di tipo e' un'azione NUOVA. */
  chiave: string;
  tipo: LimuloTipoAzione;
  tournamentId: string;
  /** `null` = azione sul torneo (il check-in al torneo). */
  matchId: string | null;
  /** Il nome del torneo, com'e' (`title`, poi `name`). */
  torneo: string;
  round: number | null;
  /** La scadenza del SERVER (ISO), per il solo testo del conto alla rovescia. */
  scadenza: string | null;
  /** Il tag dell'invito CR, solo per `invito`. */
  tag: string | null;
  /**
   * Il nome dell'altro giocatore, per la modale del check-in (`modaleCheckIn.ts`). Solo dal motore
   * (`opponentName` della voce): le sonde non lo ricostruiscono, e assente vale «non lo so».
   * Per `tipo === 'sfida'` e' invece chi sfida (`SorgenteSfidePiattaforma`).
   */
  avversario?: string | null;
  /**
   * L'`origine` del torneo (`coda`, `sit-and-go`, `sfida`, …) quando la voce del motore la porta
   * (qlash-limulo#216, kb#368 p.3): la barra e la modale decidono la presentazione da qui, senza leggere
   * il dettaglio. `null`/assente = la voce non la dice (le sonde, le sfide, un server piu' vecchio): si
   * ripiega sul dettaglio del torneo, come prima.
   */
  origine?: string | null;
  /**
   * Solo `tipo === 'sfida'`. `null` = **la riga NON ha destinazione** (la sfida di piattaforma: non ha una
   * pagina sua): il corpo e' inerte, e il filtro «non mostrare l'azione che porta dove sei» non la tocca mai
   * (`senzaDestinazione`). `undefined` resta «la calcola `destinazioneDi`» da `matchId`/`tournamentId`,
   * come per ogni riga Limulo. Il campo non porta stringhe: nessun produttore ne emette (l'unico che lo
   * assegna e' `rigaSfidaPiattaforma`, a `null`), e il ramo della destinazione esplicita, nato con la riga
   * del ponte verso il DM, e' stato tolto. Se una riga futura avra' una destinazione sua, il ramo torna
   * insieme al suo produttore.
   */
  destinazione?: null;
  /**
   * PONTE PROVVISORIO I-1317 (A7, tipo) — si toglie quando: P-a (qTournaments#225 e qlash-limulo#287 MERGED, `GET /v1/challenges/<id>` su staging porta `allenamento`, `src/lib/limulo/challenge.ts` lo manda).
   * La rotta INTERNA della conversazione col DM del ponte (`percorsoConversazione(conversationId)`), solo per la riga del ponte. Qui e' SOLO un
   * tipo: questo modulo resta puro sotto Node (tools/verify-limulo.ts, «niente import.meta.env»). La legge `AzionePersistente.tsx` nel letterale
   * del flag del coach, e solo se e' una rotta interna; altrimenti vale `destinazioneDi`, invariata.
   */
  destinazionePonteI1317?: string;
  /**
   * Solo `tipo === 'sfida'`: sostituisce il pulsante unico «Vai» con Accetta/Rifiuta — le azioni della
   * sfida di piattaforma (`useRispostaSfida`), passate gia' legate alla propria sfida. `null`/
   * assente per OGNI riga Limulo: quelle restano col loro unico «Vai», invariato.
   */
  pulsantiSfida?: {
    onAccetta: () => void;
    onRifiuta: () => void;
    accettando: boolean;
    rifiutando: boolean;
    /**
     * L'esito NEGATIVO dell'ultimo tocco, gia' localizzato, da mostrare NELLA riga (`role="alert"`) —
     * la live region nascosta non basta a chi vede (bloccante 3, revisione 19:23Z). `null`/assente =
     * nessun errore.
     */
    errore?: string | null;
    /**
     * L'esito POSITIVO del tocco («Sfida accettata…», «Sfida rifiutata»), gia' localizzato, sotto la riga
     * (`role="status"`) in TUTTI i modi — anche in `riga`, che scarta il titolo. Come `errore`: il segnale
     * che il tocco e' andato si LEGGE, non solo si annuncia. `null`/assente = niente.
     */
    esito?: string | null;
    /**
     * kb#388: la frase NEUTRA mentre il tocco aspetta il sync del profilo («Sto collegando il tuo profilo…»), gia' localizzata,
     * sotto la riga (`role="status"`, colore muto: non e' un esito riuscito, e `esito` e' verde). `null`/assente = niente.
     */
    collegando?: string | null;
  } | null;
}

/** I dati di un torneo come li leggono le sonde. `undefined` = non ancora arrivato. */
export interface LimuloDatiTorneo {
  torneo: LimuloTournament;
  iscrizione: LimuloRegistrationStatus | null | undefined;
  /** `GET /:id/matches` — gia' «i miei» (scope `mine` del bordo). */
  incontri: readonly LimuloMatch[] | undefined;
  /** I dettagli letti a parte (`GET /:id/matches/:matchId`), perche' solo loro portano `externalData`. */
  dettagli: Readonly<Record<string, LimuloMatch | undefined>>;
  /** Il `profileType` del gioco del torneo, o `null` se non lo sappiamo. */
  profileType: string | null;
  /**
   * Il QGammon di questa build (`servizioTavoliDellaBuild()`), per il tavolo del backgammon (I-189).
   * Facoltativo: assente = nessun tavolo, e il backgammon resta senza azione come prima.
   */
  servizioTavoli?: string | null;
  /** Gli scacchi su Limulo sono accesi in questa build (`chessLimuloAcceso()`): la STESSA regola della scheda (P4b). Assente = spenti. */
  scacchi?: boolean;
  /**
   * Gli id QGammon delle partite che questa sessione ha visto FINIRE (`activeMatch.finite`).
   * Un «gioca» il cui tavolo e' fra questi non si propone: a partita finita l'incontro Limulo resta
   * `pending` sul server finche' il motore non lo chiude (~45 s, prova Back_6 del 26/09), e la barra
   * diceva «INCONTRO DA GIOCARE · Vai» sulla schermata di fine partita. ⚠️ Non e' un orologio: il
   * giorno che il server pubblica un tavolo NUOVO per lo stesso incontro, l'id e' un altro e il
   * «gioca» torna. Facoltativo: assente = nessuna partita finita.
   * 🔄 **Una regola, due schermi** (seguito di #403, schermate-tornei-orcb #408): la stessa lista la
   * legge anche la scheda d'incontro — `tavoloDaAprireOf` con `finiti` (niente «Entra al tavolo») e
   * `facciaBottoneOf` con `tavoloFinito` («il risultato lo manda il gioco», solo in `provider`), via
   * `useTavoliFiniti`. Qui la barra la applica dopo `tipoAzioneIncontro`; l'esito coincide.
   */
  tavoliFiniti?: ReadonlySet<string>;
  /**
   * Tennis Clash di questa build e di questa piattaforma (`tennisClashDellaBuild()`): la STESSA regola
   * della scheda (`openGameTargetOf`), cosi' barra e bottone unico non dicono cose diverse — dove la
   * scheda offre «Apri Tennis Clash» la barra porta a «gioca», dove non c'e' un link (desktop, link
   * mancante) non porta niente. Facoltativo: assente = flag spento, Tennis Clash senza azione come prima.
   */
  tennisClash?: LimuloOpenGameTennisClash;
}

const nomeDi = (t: LimuloTournament): string => t.title || t.name || t.tournamentId || t.id;

/**
 * L'azione di UN incontro: la faccia del suo bottone unico, tradotta in un tipo.
 * `null` = niente da mettere nella barra (nessun gesto, oppure un gesto spento col motivo: la barra
 * porta cio' che si puo' FARE, il motivo lo dice la scheda).
 */
export function tipoAzioneIncontro(
  match: LimuloMatch,
  profileType: string | null,
  servizioTavoli: string | null = null,
  /** Il torneo dell'incontro: la STESSA faccia della scheda lo riceve (kb#303 §3, revisione 1 di #474). */
  torneo: LimuloTorneoDellIncontro = TORNEO_SCONOSCIUTO,
  /** Tennis Clash di questa build e piattaforma: la STESSA della scheda. Assente = spento. */
  tennisClash?: LimuloOpenGameTennisClash,
  /** Gli scacchi su Limulo sono accesi in questa build: la STESSA della scheda (`tavoloDaAprireOf`). Assente = spenti. */
  scacchi = false,
): LimuloTipoAzione | null {
  const apriGioco = openGameTargetOf({ match, profileType, tennisClash });
  const tavolo = tavoloDaAprireOf({ match, profileType, servizio: servizioTavoli, scacchi }) !== null;
  const invito = apriGioco.available
    ? crInviteOf({ externalData: match.externalData, profileType, externalMatch: match.externalMatch })
    : null;
  const stato = matchStateOf(match);
  const proposta = stato === 'proposed' ? match.result : null;
  const faccia = facciaBottoneOf({
    azioni: matchActionsOf(match, { checkinDone: false }, torneo),
    piano: matchCheckInPlanOf(match),
    stato,
    ruolo: ruoloPropostaOf(match, torneo),
    punteggioProposto: proposta !== null && proposta.scoreA !== null && proposta.scoreB !== null,
    // ⛔ Il silenzio-assenso scaduto si deduce dall'orologio: qui non serve. Cambia solo la PAROLA
    // del bottone («Confermo» / «Chiudi con questo referto»), non il tipo d'azione.
    assensoScaduto: false,
    invito,
    partitaMorta: apriGioco.block !== 'not-playable' && externalMatchDeadOf(match.externalMatch),
    apriGioco,
    tavolo,
  });
  if (faccia === null) return null;
  switch (faccia.kind) {
    case 'checkin':
      return faccia.blocco === null ? 'checkin-incontro' : null;
    case 'apri-gioco':
      return faccia.invito ? 'invito' : 'gioca';
    // Il tavolo aperto e' un incontro da giocare: la barra porta alla scheda, dove il bottone unico
    // dice «Entra al tavolo». ⚠️ Non «riapre» la barra minimizzata: non e' fra le azioni che si perdono
    // a tavolino se non le vedi (`LIMULO_AZIONI_CHE_RIAPRONO`).
    case 'tavolo':
      return 'gioca';
    case 'conferma':
      return 'conferma';
    case 'referto':
      return 'referto';
    case 'attesa':
    case 'motivo':
      // Non sono cose da fare: l'attesa e' dell'avversario, il motivo e' di qualcun altro.
      return null;
  }
}

/** Le azioni di un torneo: il check-in al torneo, poi una per incontro. */
export function azioniDelTorneo(dati: LimuloDatiTorneo): LimuloAzionePendente[] {
  const { torneo, iscrizione } = dati;
  const tournamentId = torneo.tournamentId || torneo.id;
  const nome = nomeDi(torneo);
  const out: LimuloAzionePendente[] = [];

  // Il check-in AL TORNEO: solo il `false` esplicito del server. `null`/assente e' «non lo so».
  const ck = iscrizione?.checkIn ?? null;
  if (ck !== null && ck.required && ck.open && ck.checkedIn === false) {
    out.push({
      chiave: `${tournamentId}:-:checkin-torneo`,
      tipo: 'checkin-torneo',
      tournamentId,
      matchId: null,
      torneo: nome,
      round: null,
      scadenza: null,
      tag: null,
    });
  }

  for (const riga of dati.incontri ?? []) {
    const match = dati.dettagli[riga.matchId] ?? riga;
    const tipo = tipoAzioneIncontro(
      match,
      dati.profileType,
      dati.servizioTavoli ?? null,
      torneoDellIncontroOf(torneo),
      dati.tennisClash,
      dati.scacchi ?? false,
    );
    if (tipo === null) continue;
    if (tipo === 'gioca' && dati.tavoliFiniti && dati.tavoliFiniti.size > 0) {
      const tavolo = tavoloDaAprireOf({ match, profileType: dati.profileType, servizio: dati.servizioTavoli ?? null, scacchi: dati.scacchi ?? false });
      if (tavolo !== null && dati.tavoliFiniti.has(tavolo.matchId)) continue;
    }
    const piano = matchCheckInPlanOf(match);
    const invito =
      tipo === 'invito'
        ? crInviteOf({ externalData: match.externalData, profileType: dati.profileType, externalMatch: match.externalMatch })
        : null;
    out.push({
      chiave: `${tournamentId}:${match.matchId}:${tipo}`,
      tipo,
      tournamentId,
      matchId: match.matchId,
      torneo: nome,
      round: match.roundNumber ?? null,
      scadenza: piano.kind === 'aperto' || piano.kind === 'pronto' ? piano.deadlineAt : null,
      tag: invito?.tag ?? null,
    });
  }
  return out;
}

/**
 * Tutte le azioni, dalla piu' urgente. A parita' di tipo vince la scadenza del SERVER piu' vicina
 * (confrontare due istanti del server non e' guardare l'orologio), poi la chiave: l'ordine deve
 * essere lo stesso a ogni giro, o la barra cambierebbe voce da sola.
 */
export function ordinaAzioni(azioni: readonly LimuloAzionePendente[]): LimuloAzionePendente[] {
  const rango = (t: LimuloTipoAzione) => LIMULO_TIPI_AZIONE.indexOf(t);
  return [...azioni].sort((a, b) => {
    const r = rango(a.tipo) - rango(b.tipo);
    if (r !== 0) return r;
    if (a.scadenza !== b.scadenza) {
      if (a.scadenza === null) return 1;
      if (b.scadenza === null) return -1;
      return a.scadenza < b.scadenza ? -1 : 1;
    }
    return a.chiave < b.chiave ? -1 : a.chiave > b.chiave ? 1 : 0;
  });
}

/**
 * La barra minimizzata si RIAPRE da sola? Si', se fra le azioni di adesso ce n'e' una di un tipo che
 * riapre (invito, check-in d'incontro) che non c'era quando la si e' minimizzata.
 * @param viste le chiavi presenti al momento della minimizzazione (e di ogni giro successivo).
 */
export function riapreLaBarra(viste: ReadonlySet<string>, adesso: readonly LimuloAzionePendente[]): boolean {
  return adesso.some((a) => LIMULO_AZIONI_CHE_RIAPRONO.has(a.tipo) && !viste.has(a.chiave));
}

/**
 * La riga NON ha destinazione (`destinazione: null`, la sfida di piattaforma): il corpo e' inerte e il
 * filtro «non mostrare l'azione che porta dove sei» non la tocca su nessuna pagina.
 */
export const senzaDestinazione = (a: LimuloAzionePendente): boolean => a.destinazione === null;

/**
 * Dove porta un'azione: la scheda dell'incontro, o quella del torneo per il check-in al torneo.
 * **`null` esattamente quando `senzaDestinazione`**: ogni chiamante che naviga deve gestirlo (il tipo lo
 * impone), e nessuno compone un URL per una riga che non ha un posto dove andare.
 */
export const destinazioneDi = (a: LimuloAzionePendente): string | null =>
  senzaDestinazione(a)
    ? null
    : a.matchId !== null
      ? limuloMatchUrl(a.tournamentId, a.matchId)
      : limuloTournamentUrl(a.tournamentId);

/**
 * Le azioni che accendono il bottone flottante con una partita viva (`checkin-incontro`, `invito`): solo
 * quelle che portano DA QUALCHE PARTE. Un bottone il cui tocco non va in nessun posto non compare (scelta
 * dichiarata: la riga senza destinazione oggi non e' di questi due tipi, quindi e' una difesa in piu', non
 * un caso raggiungibile — la copre un test sulla funzione).
 */
export const urgentiDelBottone = (visibili: readonly LimuloAzionePendente[]): LimuloAzionePendente[] =>
  visibili.filter((a) => (a.tipo === 'checkin-incontro' || a.tipo === 'invito') && !senzaDestinazione(a));

/**
 * La schermata e' una scheda d'incontro? Li' in fondo c'e' il bottone unico (#384, nel posto di
 * `AppShell` sotto questa barra): due strisce d'azione impilate si contendono lo stesso pollice e
 * l'inset di sicurezza, quindi sulla scheda d'incontro la barra persistente non compare — per NESSUN
 * incontro, non solo per quello aperto. ⚠️ Scelta del cantiere, dichiarata.
 */
export function eSchedaIncontro(pathname: string): boolean {
  const base = `/${LIMULO_ROUTE_PREFIX}/tournaments/`;
  if (!pathname.startsWith(base)) return false;
  const resto = pathname.slice(base.length).split('/');
  return resto.length === 3 && resto[1] === 'matches' && resto[0] !== '' && resto[2] !== '';
}

/**
 * La schermata e' il TAVOLO di una partita di scacchi? `/chess/match/:id` con un uuid: lo stesso id che il tavolo accetta
 * (`chessMatchIdValido`), perche' un id che il tavolo rifiuterebbe non e' un tavolo. Solo questa rotta: ne' `chess/live` ne'
 * `chess/replay`, dove nessuna partita e' in corso per chi guarda.
 *
 * Il backgammon sa di avere un tavolo perche' registra la partita in `ActiveMatchContext` (`descriptor`); gli scacchi no — quel
 * contesto possiede i socket di QGammon e QChinesePoker, e il socket scacchi sta nella rotta (`useChessTavolo`). Qui la barra lo
 * riconosce PER ROTTA, e ne fa lo stesso uso (v. `BarraAzioni`): sul tavolo, solo le azioni urgenti.
 */
export function eTavoloScacchi(pathname: string): boolean {
  const m = /^\/chess\/match\/([^/]+)\/?$/.exec(pathname);
  return m !== null && chessMatchIdValido(m[1]);
}

/**
 * La schermata e' il TAVOLO VIVO di una partita di bridge? `/bridge/tavolo/:id` con un uuid (`bridgeTableIdValido`: lo stesso id che il tavolo accetta).
 * Solo questa rotta, non `/bridge/pratica` (la scelta del livello: nessuna partita in corso) ne' le rotte della pratica locale. K9b-1 (condizione 8 di
 * tma-orcr): il socket sta nella rotta come per gli scacchi, quindi la barra lo riconosce PER ROTTA e sul tavolo mostra solo le azioni urgenti.
 */
export function eTavoloBridge(pathname: string): boolean {
  const m = /^\/bridge\/tavolo\/([^/]+)\/?$/.exec(pathname);
  return m !== null && bridgeTableIdValido(m[1]);
}

/**
 * I TAVOLI VIVI PER ROTTA, la lista ESATTA: i giochi il cui socket sta nella rotta e non in `ActiveMatchContext` (che possiede quelli di QGammon e
 * QChinesePoker). Sono due: gli scacchi (`/chess/match/:id`) e il bridge (`/bridge/tavolo/:id`). Chi aggiunge un tavolo lo aggiunge QUI, e
 * `tests/azione-persistente-tavoli-vivi.test.ts` fissa l'elenco: la barra ridotta alle urgenti e una regola di tutti, non un terzo comportamento (I-556).
 */
export const TAVOLI_VIVI_PER_ROTTA: readonly ((pathname: string) => boolean)[] = [eTavoloScacchi, eTavoloBridge];
export const eTavoloVivoPerRotta = (pathname: string): boolean => TAVOLI_VIVI_PER_ROTTA.some((e) => e(pathname));

/**
 * C'e' una finestra di check-in d'incontro APERTA per il SERVER (da fare, o fatto e in attesa
 * dell'avversario)? E' l'unica cosa che porta il ritmo di un torneo a 12 s: ha una scadenza del
 * server, e il prossimo stato (l'invito) arriva entro minuti. ⛔ Non l'invito: in `provider` resta
 * «partito» fino al risultato, e 12 s perpetui sarebbero traffico senza scadenza (punto 2).
 */
export function finestraCheckInAperta(dati: Pick<LimuloDatiTorneo, 'incontri' | 'dettagli'>): boolean {
  return (dati.incontri ?? []).some((riga) => {
    const k = matchCheckInPlanOf(dati.dettagli[riga.matchId] ?? riga).kind;
    return k === 'aperto' || k === 'pronto';
  });
}

/**
 * Quali tornei si sondano, e come (revisione #385, punto 1). `/v1/tournaments/mine` ordina per data
 * di CREAZIONE, e un tetto applicato su quell'ordine lasciava fuori il torneo in corso dietro cinque
 * iscrizioni piu' recenti.
 *  · in corso (o in pausa): sonda COMPLETA — iscrizione e incontri;
 *  · gli altri non terminali (non iniziati, o uno stato che non riconosciamo): solo l'ISCRIZIONE —
 *    prima dell'inizio l'unica azione possibile e' il check-in al torneo, e gli incontri non ci sono;
 *  · oltre il tetto, per ciascun gruppo: CONTATI, e la barra lo dice. Niente silenzio.
 */
export function divisioneSonde<T extends { status: string }>(
  vivi: readonly T[],
  tetto: number,
): { completi: T[]; soloIscrizione: T[]; nonControllati: number } {
  const inCorso = vivi.filter((t) => t.status === 'in_progress' || t.status === 'paused');
  const altri = vivi.filter((t) => !inCorso.includes(t));
  const completi = inCorso.slice(0, tetto);
  const soloIscrizione = altri.slice(0, tetto);
  return { completi, soloIscrizione, nonControllati: vivi.length - completi.length - soloIscrizione.length };
}

/**
 * LA CODA NELLA BARRA (decisione di Luca 2026-09-25 ~23:2xZ, «B1 (b)»): mentre si e' in coda di
 * qTournaments, in tutta l'app la barra dice «In coda · <tempo> · Esci».
 *
 * Le regole, e l'ordine E' la logica:
 *  · un'azione VERA vince sempre (check-in d'incontro, invito, tavolo, referto…): la coda si mostra solo
 *    quando la barra non ha niente da fare;
 *  · con una partita viva (`descriptor`) la barra resta quella della partita: la coda non si mostra;
 *  · sulla «Ranked» della Hub CR, dove c'e' il pannello delle code, la riga ripeterebbe il pannello — e
 *    sul «Sit&Go» a flag acceso, che racconta lo stesso biglietto (`suPannelloCode`);
 *  · `waiting` ⇒ «In coda» col tempo dal `since` piu' vecchio; `matched` ⇒ «Partita trovata…» fino al
 *    passaggio all'incontro; `idle` ⇒ niente — da li' parla l'azione dell'incontro nuovo.
 * ⚠️ La coda non riapre la barra minimizzata: la riga compare solo quando non ci sono azioni, cioe'
 * quando la barra non e' minimizzata da nessuno (`BarraAzioni` azzera `minimizzata` a lista vuota).
 */
export type LimuloRigaCoda = { kind: 'in-coda'; da: number | null } | { kind: 'abbinato' };

export function rigaCodaOf(input: {
  ticket: LimuloQueueTicket | null;
  /** Quante azioni la barra mostrerebbe adesso. */
  azioni: number;
  partitaViva: boolean;
  /** Si sta guardando un pannello che racconta gia' il biglietto: la «Ranked», o il «Sit&Go» a flag. */
  suPannelloCode: boolean;
}): LimuloRigaCoda | null {
  if (input.azioni > 0 || input.partitaViva || input.suPannelloCode) return null;
  const momento = queueMomentOf(input.ticket);
  if (momento.kind === 'in-attesa') return { kind: 'in-coda', da: waitingSinceOf(momento.queues) };
  if (momento.kind === 'abbinato') return { kind: 'abbinato' };
  return null;
}

/**
 * «Vai» della riga della coda ha dove portare? Il biglietto sa a che gioco appartiene (`gameId`), e la Hub ha
 * le schede delle code solo per i giochi che le montano (`conScheda`: i `profileType`): un biglietto di un
 * gioco senza scheda NON porta alla Hub di Clash Royale — la riga resta, con «Esci», e «Vai» no.
 * ⚠️ Fail-open su cio' che non si sa: catalogo non letto, o gioco che il catalogo non nomina ⇒ vero, com'e'
 * sempre stato (il solo gioco con le code era CR). Si toglie «Vai» solo a catalogo letto che dice un altro gioco.
 */
export function codaHaUnaScheda(input: {
  ticket: LimuloQueueTicket | null;
  games: readonly { gameId: string; profileType: string }[] | null | undefined;
  conScheda: readonly string[];
}): boolean {
  if (input.ticket === null || !Array.isArray(input.games)) return true;
  const gioco = input.games.find((g) => g.gameId === input.ticket?.gameId);
  return gioco === undefined ? true : input.conScheda.includes(gioco.profileType);
}

/**
 * Il `profileType` del gioco di un biglietto, dal catalogo (`GET /v1/games`); `null` se il catalogo non c'e' o non
 * nomina quel gioco — e allora «Vai» e il nascondersi della barra restano quelli di Clash Royale, come prima.
 */
export function profileTypeDelBiglietto(
  ticket: LimuloQueueTicket | null,
  games: readonly { gameId: string; profileType: string }[] | null | undefined,
): string | null {
  if (ticket === null || !Array.isArray(games)) return null;
  return games.find((g) => g.gameId === ticket.gameId)?.profileType ?? null;
}

/**
 * La riga della coda si ripeterebbe: si sta guardando il pannello che racconta QUEL biglietto?
 *  · biglietto di Clash Royale: la «Ranked» della Hub, o il «Sit&Go» col suo flag — ma non col backgammon attivo,
 *    dove la scheda `'ranked'` e' la «Gioca» del backgammon (la coda nativa di QGammon), che di quel biglietto non
 *    dice niente;
 *  · biglietto del backgammon: il suo «Sit&Go», e solo col backgammon attivo.
 * Sempre e solo sulla Hub (`/`): altrove la riga serve.
 */
export function suPannelloDellaCodaOf(input: {
  gioco: 'cr' | 'bg';
  pathname: string;
  sezioneHub: string | null;
  backgammonAttivo: boolean;
  sitngoCrAcceso: boolean;
}): boolean {
  if (input.pathname !== '/') return false;
  if (input.gioco === 'bg') return input.backgammonAttivo && input.sezioneHub === 'sitngo';
  return (
    !input.backgammonAttivo &&
    (input.sezioneHub === 'ranked' || (input.sitngoCrAcceso && input.sezioneHub === 'sitngo'))
  );
}

/**
 * Dove porta «Vai» della riga della coda: il pannello della Hub CR in cui si aspetta. Il biglietto e'
 * uno per gioco e nomina le code per id; il TIPO (1v1 o sit&go) sta nella vetrina.
 *  · `sitngo` solo col pannello Sit&Go acceso e se OGNI attesa e' un sit&go (da abbinati: il modello
 *    dell'abbinamento, `matched.queueId`);
 *  · altrimenti `ranked`, come prima: anche a vetrina non letta (`queues === null`) o con un id che la
 *    vetrina non ha — la Ranked racconta ogni attesa per nome, il Sit&Go no (lista dei soli sit&go).
 */
export type SezioneDellaCoda = 'ranked' | 'sitngo';

export function sezioneDellaCodaOf(input: {
  ticket: LimuloQueueTicket | null;
  queues: readonly { queueId: string; kind: string }[] | null;
  sitngoAcceso: boolean;
}): SezioneDellaCoda {
  const { ticket, queues } = input;
  if (!input.sitngoAcceso || ticket === null || queues === null) return 'ranked';
  const tipo = new Map(queues.map((q) => [q.queueId, q.kind]));
  const ids =
    ticket.state === 'matched' && ticket.matched?.queueId
      ? [ticket.matched.queueId]
      : ticket.queues.map((q) => q.queueId);
  return ids.length > 0 && ids.every((id) => tipo.get(id) === 'sit-and-go') ? 'sitngo' : 'ranked';
}

/**
 * 🔑 **LE AZIONI DAL MOTORE, in UNA lettura** (kb#275, ADR-0167) — dietro
 * `VITE_FEATURE_TOURNAMENT_ACTIONS_ENABLED`, acceso solo in `.env.staging`. A flag spento la barra
 * resta la derivazione qui sopra (le sonde di `AzionePersistente`), identica.
 *
 * `GET /v1/me/tournaments/actions` (`qlash-limulo@7e0eae2`, `router.js:2151-2154`): la rotta non prende
 * parametri, e la busta e' `{items, truncated, at}`. Il BFF lascia passare 11 chiavi per voce e ne
 * toglie ogni valore che non sia stringa, booleano, numero finito o `null` (`tournament-actions.js:3-12`):
 * una chiave MANCANTE qui vale `null`. A queste si aggiunge `origine` (qlash-limulo#216, `a3f759c`), ma solo
 * per i cinque valori pubblici di qT (`manuale|coda|sit-and-go|sfida|community`): con ogni altro valore la
 * chiave non c'e'. Il valore gia' filtrato dal server qui non si ri-filtra: passa com'e', come stringa. Le voci le decide il motore (`azioni.js` di qTournaments 1.75.x)
 * con gli stessi verdetti dei permessi della scheda, e nell'ORDINE del motore (urgenza, poi scadenza).
 * La barra mostra quell'ordine: `ordinaAzioni` qui non si applica.
 *
 * Cosa resta del client, e solo questo (dispatch tma-orcb-7-azioni):
 *  · la barra porta cio' che si puo' FARE: `actionable === true`, fra i cinque kind noti. Le attese
 *    (`awaiting-*`, `disputed`, `game-dead`, `double-no-show`) restano fuori, come oggi;
 *  · `tavoliFiniti` (#403): un «gioca» il cui tavolo QGammon questa sessione ha visto finire non si
 *    propone. Il tavolo e' `inviteRef`: il motore lo prende da `externalData.opened.providerData.matchId`,
 *    la stessa risposta del provider da cui scrive `externalTableId` (`partite-esterne.js:501, 810, 816`),
 *    cioe' l'id che la scheda mette in `activeMatch.finite`;
 *  · il conto alla rovescia SOLO sul check-in d'incontro, come oggi (tma-orcb 08:20Z, A4). Il motore da'
 *    anche la scadenza della conferma e del check-in al torneo: qui non si usano.
 * ⛔ Nessun orologio (ADR-0158): niente qui guarda l'ora; `at` del motore non entra in nessun ramo.
 */
export const LIMULO_AZIONI_PATH = '/v1/me/tournaments/actions';

/** Una voce del motore, com'e' arrivata. Le chiavi che la barra non usa non si portano. */
export interface LimuloVoceAzione {
  tournamentId: string;
  tournamentName: string | null;
  matchId: string | null;
  round: number | null;
  kind: string;
  actionable: boolean;
  deadlineAt: string | null;
  /** Il riferimento nel gioco (ADR-0160): su Clash Royale il tag dell'invito, su QGammon il tavolo. */
  inviteRef: string | null;
  /** Il nome dell'altro giocatore, se il motore lo sa: lo mostra la modale del check-in. */
  opponentName: string | null;
  /**
   * Da dove e' nato il torneo (qlash-limulo#216, kb#368 p.3): il BFF la porta SOLO per i cinque valori
   * pubblici di qT (`manuale|coda|sit-and-go|sfida|community`), altrimenti la chiave non c'e'. Qui e' una
   * stringa e basta: il valore che il server ha gia' filtrato non si ri-filtra sui cinque. `null` = la
   * voce non la porta (server precedente alla #216, o un valore non pubblico).
   */
  origine: string | null;
}

export interface LimuloAzioniDelMotore {
  items: LimuloVoceAzione[];
  /** Oltre i tetti del motore (50 tornei, 200 incontri): la lista non e' completa, e lo dice. */
  truncated: boolean;
}

const testoONull = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

/**
 * La busta del BFF → le voci. `null` se la risposta non ha la forma del contratto: la barra allora
 * tace, come per un errore (non e' «nessuna azione»). Una voce senza torneo o senza kind si scarta.
 */
export function azioniDelMotoreOf(envelope: unknown): LimuloAzioniDelMotore | null {
  const busta = envelope !== null && typeof envelope === 'object' ? (envelope as Record<string, unknown>) : null;
  const data = busta !== null && busta.data !== null && typeof busta.data === 'object' ? (busta.data as Record<string, unknown>) : null;
  if (data === null || !Array.isArray(data.items)) return null;
  const items: LimuloVoceAzione[] = [];
  for (const grezza of data.items) {
    if (grezza === null || typeof grezza !== 'object') continue;
    const r = grezza as Record<string, unknown>;
    const tournamentId = testoONull(r.tournamentId);
    const kind = testoONull(r.kind);
    if (tournamentId === null || kind === null) continue;
    items.push({
      tournamentId,
      tournamentName: testoONull(r.tournamentName),
      matchId: testoONull(r.matchId),
      round: typeof r.round === 'number' && Number.isFinite(r.round) ? r.round : null,
      kind,
      actionable: r.actionable === true,
      deadlineAt: testoONull(r.deadlineAt),
      inviteRef: testoONull(r.inviteRef),
      opponentName: testoONull(r.opponentName),
      origine: testoONull(r.origine),
    });
  }
  return { items, truncated: data.truncated === true };
}

/**
 * Il kind del motore → il tipo della barra. `game-invite` dipende dal gioco: v. `azioniDalMotore`.
 * ⚠️ Una `Map`, non un oggetto letterale (revisione di #422): il kind arriva dal server, e su un oggetto
 * `TIPO_DEL_KIND['constructor']` rende la funzione `Object` — un «tipo» che la barra disegnerebbe.
 * Con la `Map` un kind che non e' nostro (`constructor`, `__proto__`, `toString`) e' `undefined`, e si scarta.
 */
const TIPO_DEL_KIND: ReadonlyMap<string, LimuloTipoAzione> = new Map<string, LimuloTipoAzione>([
  ['match-checkin', 'checkin-incontro'],
  ['result-confirm', 'conferma'],
  ['tournament-checkin', 'checkin-torneo'],
  ['result-submit', 'referto'],
]);

/**
 * I tornei di cui serve il GIOCO: solo quelli con un invito, perche' `inviteRef` e' un tag su Clash
 * Royale («Invito · #TAG») e un tavolo su QGammon («Incontro da giocare»). La voce non porta il gioco.
 */
export function torneiDaRiconoscere(voci: readonly LimuloVoceAzione[]): string[] {
  return [...new Set(voci.filter((v) => v.actionable && v.kind === 'game-invite').map((v) => v.tournamentId))];
}

/**
 * Le voci del motore → le azioni della barra, nell'ordine del motore.
 * @param profiloDi il `profileType` del gioco di un torneo, o `null` se non lo sappiamo ancora — e
 *   allora l'invito e' un «gioca», com'era la barra finche' il catalogo non arrivava.
 */
export function azioniDalMotore(input: {
  voci: readonly LimuloVoceAzione[];
  profiloDi: (tournamentId: string) => string | null;
  tavoliFiniti?: ReadonlySet<string>;
}): LimuloAzionePendente[] {
  const out: LimuloAzionePendente[] = [];
  const viste = new Set<string>();
  for (const v of input.voci) {
    if (!v.actionable) continue;
    let tipo: LimuloTipoAzione | undefined;
    if (v.kind === 'game-invite') {
      tipo = input.profiloDi(v.tournamentId) === 'clash-royale' && v.inviteRef !== null ? 'invito' : 'gioca';
    } else {
      tipo = TIPO_DEL_KIND.get(v.kind);
    }
    if (tipo === undefined) continue;
    if (tipo === 'gioca' && v.inviteRef !== null && input.tavoliFiniti?.has(v.inviteRef)) continue;
    const chiave = `${v.tournamentId}:${v.matchId ?? '-'}:${tipo}`;
    if (viste.has(chiave)) continue;
    viste.add(chiave);
    out.push({
      chiave,
      tipo,
      tournamentId: v.tournamentId,
      matchId: v.matchId,
      torneo: v.tournamentName ?? v.tournamentId,
      round: v.round,
      scadenza: tipo === 'checkin-incontro' ? v.deadlineAt : null,
      tag: tipo === 'invito' ? v.inviteRef : null,
      avversario: v.opponentName,
      origine: v.origine,
    });
  }
  return out;
}

/**
 * La finestra di check-in d'incontro e' APERTA per il server (da fare, o fatto e in attesa
 * dell'avversario)? E' la stessa domanda di `finestraCheckInAperta`, sulle voci del motore: porta la
 * lettura a 12 s. Guarda anche le attese (`awaiting-opponent`), che la barra non mostra.
 */
export function finestraCheckInApertaDalMotore(voci: readonly LimuloVoceAzione[]): boolean {
  return voci.some((v) => v.kind === 'match-checkin' || v.kind === 'awaiting-opponent');
}
